import React, { useMemo } from 'react'

import { useQuery } from '@apollo/client'

import { GET_CURRENT_CUSTOMER_ORDERS, GET_CURRENT_CUSTOMER_RESERVATIONS } from 'Const'

import { ProfileTabKey } from '../../TabNav'
import { OrderCard } from '../Orders/OrderCard'
import { Order } from '../Orders/types'
import { groupOrders } from '../Orders/utils'
import { ReservationCard } from '../Reservations/Card'
import { Reservation } from '../Reservations/types'
import { groupReservations } from '../Reservations/utils'

const RECENT_ORDERS_LIMIT = 2
const UPCOMING_RESERVATIONS_LIMIT = 2

interface SectionProps {
  actionLabel: string
  children: React.ReactNode
  onAction: () => void
  title: string
}

const Section: React.FC<SectionProps> = ({ actionLabel, children, onAction, title }) => (
  <section className="user-profile-section">
    <div className="user-profile-section__header">
      <h2 className="user-profile-section__title">{title}</h2>
      <button className="user-profile-link-button" type="button" onClick={onAction}>
        {actionLabel}
      </button>
    </div>
    {children}
  </section>
)

interface HomeTabProps {
  onNavigateTab: (tab: ProfileTabKey) => void
}

export const HomeTab: React.FC<HomeTabProps> = ({ onNavigateTab }) => {
  const ordersResult = useQuery(GET_CURRENT_CUSTOMER_ORDERS)
  const reservationsResult = useQuery(GET_CURRENT_CUSTOMER_RESERVATIONS)

  const orders: Order[] = useMemo(() => ordersResult.data?.currentCustomer?.orders ?? [], [ordersResult.data])
  const reservations: Reservation[] = useMemo(
    () => reservationsResult.data?.currentCustomer?.reservations ?? [],
    [reservationsResult.data],
  )

  const { active: activeOrders, upcoming: upcomingOrders, history } = useMemo(() => groupOrders(orders), [orders])
  const { upcoming: upcomingReservations } = useMemo(() => groupReservations(reservations), [reservations])

  const recentOrders = history.slice(0, RECENT_ORDERS_LIMIT)
  const nextReservations = upcomingReservations.slice(0, UPCOMING_RESERVATIONS_LIMIT)

  const loading = ordersResult.loading || reservationsResult.loading
  const error = ordersResult.error ?? reservationsResult.error
  const isEmpty = [nextReservations, activeOrders, upcomingOrders, recentOrders].every((group) => group.length === 0)

  // Every section is hidden when it has nothing in it, so an account with no
  // history at all would otherwise render a blank page.
  const body = isEmpty ? (
    <div className="user-profile-empty">
      <p className="user-profile-empty__title">Nothing here yet</p>
      <p className="user-profile-empty__text">Your reservations and orders will show up here once you have some.</p>
    </div>
  ) : (
    <>
      {nextReservations.length > 0 ? (
        <Section actionLabel="View All" title="Upcoming Reservations" onAction={() => onNavigateTab('reservations')}>
          <div className="user-profile-reservation-list">
            {nextReservations.map((reservation) => (
              <ReservationCard key={reservation.id} reservation={reservation} />
            ))}
          </div>
        </Section>
      ) : null}

      {activeOrders.length > 0 ? (
        <Section actionLabel="View All" title="Active Orders" onAction={() => onNavigateTab('orders')}>
          <div className="user-profile-order-list">
            {activeOrders.map((order) => (
              <OrderCard key={order.uuid ?? order.id} order={order} />
            ))}
          </div>
        </Section>
      ) : null}

      {upcomingOrders.length > 0 ? (
        <Section actionLabel="View All" title="Upcoming Orders" onAction={() => onNavigateTab('orders')}>
          <div className="user-profile-order-list">
            {upcomingOrders.map((order) => (
              <OrderCard key={order.uuid ?? order.id} order={order} />
            ))}
          </div>
        </Section>
      ) : null}

      {recentOrders.length > 0 ? (
        <Section actionLabel="View History" title="Recent Orders" onAction={() => onNavigateTab('orders')}>
          <div className="user-profile-order-list">
            {recentOrders.map((order) => (
              <OrderCard key={order.uuid ?? order.id} order={order} />
            ))}
          </div>
        </Section>
      ) : null}
    </>
  )

  return (
    <div className="user-profile-panel user-profile-panel_enter">
      {loading ? (
        <div className="user-profile-query-state user-profile-query-state_loading">Loading your activity…</div>
      ) : error ? (
        <div className="user-profile-query-state user-profile-query-state_error">Something went wrong.</div>
      ) : (
        body
      )}
    </div>
  )
}
